// src/pages/InvestingAILandingPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { getAuth } from 'firebase/auth';
import { trackEvent } from '../utils/analytics/trackEvent.js';
import Footer from '../components/Footer.jsx';
import Lottie from "lottie-react";
import tapAnimation from "../assets/animations/coin_sparkle.json";
import { FaChartPie, FaBalanceScale, FaPiggyBank, FaLightbulb } from 'react-icons/fa';
import { askAhaMomentAI } from '../api/askAhaMomentAI.js';

const InvestingAILanding = () => {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    trackEvent('aha_page_view', {
      screen: 'InvestingAILanding',
      channel: 'web',
      timestamp: Date.now(),
    });
  }, []);

  const lightColors = ["#DFFFE2", "#D6F0FF", "#FFF3E0", "#E8E8FF", "#F9F9F9"];

  const suggestionGroups = [
    {
      title: "💡 Fundamentals",
      items: [
        { text: "What are stocks?", icon: <FaChartPie /> },
        { text: "How do dividends work?", icon: <FaPiggyBank /> },
        { text: "What are ETFs?", icon: <FaBalanceScale /> },
        { text: "How to read financial statements?", icon: <FaLightbulb /> },
      ]
    },
    {
      title: "💰 Portfolio",
      items: [
        { text: "How to diversify?", icon: <FaBalanceScale /> },
        { text: "What’s asset allocation?", icon: <FaChartPie /> },
        { text: "How to manage risk?", icon: <FaPiggyBank /> },
        { text: "What are index funds?", icon: <FaChartPie /> },
      ]
    },
    {
      title: "🧠 Psychology",
      items: [
        { text: "How to control emotions in investing?", icon: <FaLightbulb /> },
        { text: "How to avoid FOMO?", icon: <FaLightbulb /> },
        { text: "Why patience matters in investing?", icon: <FaBalanceScale /> },
        { text: "How to stay consistent long-term?", icon: <FaPiggyBank /> },
      ]
    },
    {
      title: "📈 Advanced",
      items: [
        { text: "Value vs growth investing?", icon: <FaChartPie /> },
        { text: "How to read P/E ratio?", icon: <FaLightbulb /> },
        { text: "What is dollar-cost averaging?", icon: <FaPiggyBank /> },
        { text: "What are bear and bull markets?", icon: <FaChartPie /> },
      ]
    }
  ];

  const handleAskAI = async (overrideInput) => {
    const usermessage = (overrideInput || input).trim();
    if (!usermessage) return;
    setLoading(true);

    const chatSessionId = uuidv4();
    const user = auth.currentUser;
    const userId = user?.uid || 'anonymous';

    trackEvent('aha_ask_ai_button_clicked', {
      screen: 'InvestingAILanding',
      user_id: userId,
      channel: 'web',
      message_length: usermessage.length,
      timestamp: Date.now(),
    });

    navigate(`/learn/investing/space/${chatSessionId}`, {
      state: { question: usermessage },
    });

    askAhaMomentAI({
      userId,
      chatsessionid: chatSessionId,
      usermessage,
      messageCount: 0,
    }).catch(console.warn);

    setLoading(false);
  };

  return (
    <>
      {/* Fixed Top Logo Bar */}
      <div style={{ background: '#232830', padding: '0.8rem 1.2rem', display: 'flex', alignItems: 'center', height: '60px', position: 'fixed', top: 0, width: '100%', zIndex: 1000 }}>
        <img src="/assets/fintorro-logo.png" alt="FinTorro" style={{ height: '36px' }} />
      </div>

      {/* Hero Section */}
      <div style={{ background: '#232830', color: '#fff', minHeight: 'calc(100vh - 100px)', paddingTop: '80px', fontFamily: 'Nunito Sans, sans-serif' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 1rem' }}>
          <h1 style={{ fontSize: '2.4rem', fontWeight: '800', textAlign: 'center' }}>💹 Ready to Build Wealth with AI?</h1>
          <p style={{ fontSize: '1.2rem', textAlign: 'center', marginBottom: '2rem', color: '#FFFFFF'  }}>Ask anything about stocks, funds, or strategies — your AI guide will explain simply.</p>

          {/* Text Input */}
          <div style={{ position: 'relative', marginBottom: '1rem' }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. How to start investing with $100?"
              rows={5}
              style={{
                width: '100%', padding: '1rem', borderRadius: '16px', fontSize: '1.1rem',
                border: '1px solid #ccc', color: '#fff', backgroundColor: '#2C2F36'
              }}
            />
            <button
              onClick={() => handleAskAI()}
              style={{
                position: 'absolute', bottom: '12px', right: '12px',
                padding: '0.75rem 1.25rem', fontWeight: '700',
                backgroundColor: '#FF7C24', color: '#fff', borderRadius: '10px', border: 'none'
              }}
            >
              {loading ? 'Thinking...' : 'Ask AI'}
            </button>
          </div>

          {/* Suggestion Grid */}
          <div style={{ display: 'grid', gap: '2rem' }}>
            {suggestionGroups.map((group, gIdx) => (
              <div key={gIdx}>
                <h3 style={{ color: '#fff', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {group.title}
                  <Lottie animationData={tapAnimation} loop style={{ width: 32, height: 32 }} />
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
                  {group.items.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleAskAI(item.text)}
                      style={{
                        background: lightColors[idx % lightColors.length],
                        color: '#232830',
                        borderRadius: '12px',
                        padding: '0.9rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease-in-out',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
                      onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                      <div style={{ fontSize: '1.6rem' }}>{item.icon}</div>
                      <div style={{ textAlign: 'center' }}>{item.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default InvestingAILanding;